import axiosInstance from '../api/axiosInstance'
import Swal from 'sweetalert2'
import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import Modal from '../Component/Theme/Modal'
import MedicalTemplateNotesForm from '../Component/Forms/MedicalTemplateNotesForm'
import MedicalTemplatePrescriptionForm from '../Component/Forms/MedicalTemplatePrescriptionForm'

const COLORS = { gray: '#5B6B79', blue: '#96C5F7', green: '#2CA87F', red: '#9E2B25' }
const thStyle = { fontSize: 11, fontWeight: 700, color: COLORS.gray, textTransform: 'uppercase', letterSpacing: '0.05em', padding: '8px 10px', background: '#f8f9fb', borderBottom: '1px solid #e8ecf0', whiteSpace: 'nowrap' }
const tdStyle = { fontSize: 13, color: '#2d3748', padding: '7px 10px', verticalAlign: 'middle', borderBottom: '1px solid #f0f2f5' }

const SearchBox = ({ value, onChange }) => (
  <div style={{ position: 'relative', maxWidth: 260 }}>
    <i className="ti ti-search" style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: COLORS.gray, fontSize: 14, pointerEvents: 'none' }} />
    <input type="text" value={value} onChange={onChange} placeholder="Search..." style={{ paddingLeft: 32, paddingRight: 10, height: 34, fontSize: 13, border: '1px solid #dde3eb', borderRadius: 8, width: '100%', outline: 'none', color: COLORS.gray }} />
  </div>
)

const FORM_TITLE_MAP = { medical_notes: 'Medical Notes', prescriptions: 'Prescription' }

export default function MedicalTemplates({ title, templateType }) {
  const navigate = useNavigate()
  const [items, setItems] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [search, setSearch] = useState('')
  const [totalPages, setTotalPages] = useState(0)
  const [currentPage, setCurrentPage] = useState(1)
  const [modalOpen, setModalOpen] = useState(false)
  const [modalTitle, setModalTitle] = useState('')
  const [formInitialValues, setFormInitialValues] = useState({})
  const [saveTriggerCount, setSaveTriggerCount] = useState(0)

  const fetchItems = (searchTerm, page) => {
    let url = `medicalTemplates/all/${templateType}`
    const params = []
    if (searchTerm) params.push(`keyword=${searchTerm}`)
    if (page) params.push(`page=${page}`)
    if (params.length) url += `?${params.join('&')}`
    setIsLoading(true)
    axiosInstance.get(url)
      .then(res => {
        setItems(res.data.data || [])
        const tp = parseInt(res.data.totalPages)
        setTotalPages(isNaN(tp) ? 0 : tp)
      })
      .catch(() => Swal.fire('Error', 'Failed to fetch items. Please try again later.', 'error'))
      .finally(() => setIsLoading(false))
  }

  useEffect(() => {
    const handler = setTimeout(() => fetchItems(search, currentPage), 300)
    return () => clearTimeout(handler)
  }, [search, currentPage])

  const openAdd = () => {
    setModalTitle(`Add ${FORM_TITLE_MAP[templateType]}`)
    setSaveTriggerCount(0)
    setFormInitialValues({})
    setModalOpen(true)
  }

  const openEdit = item => {
    setModalTitle(`Edit ${FORM_TITLE_MAP[templateType]}`)
    setSaveTriggerCount(0)
    setFormInitialValues(item)
    setModalOpen(true)
  }

  const onSaveComplete = success => {
    if (success) {
      setModalOpen(false)
      fetchItems(search, currentPage)
    }
  }

  const handleDelete = id => {
    Swal.fire({ title: 'Are you sure?', text: 'Delete this template!', icon: 'warning', showCancelButton: true, confirmButtonColor: '#3085d6', cancelButtonColor: '#d33', confirmButtonText: 'Yes, delete it!' })
      .then(result => {
        if (result.isConfirmed)
          axiosInstance.delete(`medicalTemplates/delete/${id}`)
            .then(res => {
              if (res.data.success) { Swal.fire('Deleted!', 'The template has been deleted.', 'success'); fetchItems(search, currentPage) }
              else Swal.fire('Error', res.data.message || 'Failed to delete template.', 'error')
            })
            .catch(() => Swal.fire('Error', 'Failed to delete template.', 'error'))
      })
  }

  return (
    <div className="pc-container">
      <Modal
        title={modalTitle}
        additionalClasses="modal-fullscreen full-screen-modal-custom"
        modalOpen={modalOpen}
        onModalClose={() => setModalOpen(false)}
        onModalSave={() => setSaveTriggerCount(prev => prev + 1)}
      >
        {templateType === 'medical_notes' && <MedicalTemplateNotesForm saveTrigger={saveTriggerCount} initialValues={formInitialValues} onSave={onSaveComplete} />}
        {templateType === 'prescriptions' && <MedicalTemplatePrescriptionForm saveTrigger={saveTriggerCount} initialValues={formInitialValues} onSave={onSaveComplete} />}
      </Modal>

      <div className="pc-content">
        <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e8ecf0', overflow: 'hidden' }}>
          <div style={{ padding: '14px 20px', borderBottom: '1px solid #e8ecf0', display: 'flex', alignItems: 'center', gap: 10 }}>
            <button onClick={() => navigate(-1)} title="Go back" style={{ width: 30, height: 30, borderRadius: 8, border: '1px solid #e8ecf0', background: '#f8f9fb', color: COLORS.gray, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15, marginRight: 4, flexShrink: 0 }} onMouseEnter={e => { e.currentTarget.style.background = COLORS.blue + '22'; e.currentTarget.style.color = COLORS.blue }} onMouseLeave={e => { e.currentTarget.style.background = '#f8f9fb'; e.currentTarget.style.color = COLORS.gray }}><i className="ti ti-arrow-left" /></button>
            <div style={{ width: 32, height: 32, borderRadius: 8, background: `${COLORS.blue}20`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><i className="ti ti-files" style={{ color: COLORS.blue, fontSize: 17 }} /></div>
            <div>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#1a202c' }}>{title}</div>
              <div style={{ fontSize: 11, color: COLORS.gray }}>Manage {title?.toLowerCase()} templates</div>
            </div>
          </div>

          <div style={{ padding: '10px 20px', borderBottom: '1px solid #e8ecf0', background: '#fafbfc', display: 'flex', alignItems: 'center', gap: 8 }}>
            <button className="bgBtn btn-sm d-flex align-items-center gap-1" style={{ fontSize: 13, padding: '5px 12px' }} onClick={openAdd}><i className="ti ti-plus" style={{ fontSize: 15 }} /> Add Template</button>
          </div>

          <div style={{ padding: '14px 20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, marginBottom: 10 }}>
              <SearchBox value={search} onChange={e => { setSearch(e.target.value); setCurrentPage(1) }} />
              <span style={{ fontSize: 11, color: COLORS.gray }}>{items.length} item{items.length !== 1 ? 's' : ''}</span>
            </div>
            <div style={{ overflowX: 'auto', borderRadius: 8, border: '1px solid #e8ecf0' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={{ ...thStyle, width: 50, borderLeft: `3px solid ${COLORS.blue}` }}>#</th>
                    <th style={thStyle}>Title</th>
                    <th style={thStyle}>User</th>
                    <th style={{ ...thStyle, width: 120, textAlign: 'center' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {isLoading && <tr><td colSpan={4} style={{ ...tdStyle, textAlign: 'center', padding: '32px 0', color: COLORS.gray }}><i className="ti ti-loader-2" style={{ fontSize: 22, display: 'block', marginBottom: 6, opacity: 0.5 }} />Loading...</td></tr>}
                  {!isLoading && items.length === 0 && <tr><td colSpan={4} style={{ ...tdStyle, textAlign: 'center', padding: '32px 0', color: COLORS.gray }}><i className="ti ti-database-off" style={{ fontSize: 28, display: 'block', marginBottom: 6, opacity: 0.4 }} />No items found</td></tr>}
                  {!isLoading && items.map((item, i) => (
                    <tr key={item.id} style={{ background: i % 2 === 0 ? '#fff' : '#fafbfc' }} onMouseEnter={e => e.currentTarget.style.background = `${COLORS.blue}0d`} onMouseLeave={e => e.currentTarget.style.background = i % 2 === 0 ? '#fff' : '#fafbfc'}>
                      <td style={tdStyle}>{(currentPage - 1) * 10 + i + 1}</td>
                      <td style={{ ...tdStyle, fontWeight: 500 }}>{item.name}</td>
                      <td style={{ ...tdStyle, color: COLORS.gray }}>{item.username}</td>
                      <td style={{ ...tdStyle, textAlign: 'center' }}>
                        <div style={{ display: 'flex', gap: 6, justifyContent: 'center' }}>
                          <button onClick={() => openEdit(item)} title="Edit" style={{ width: 30, height: 30, borderRadius: 8, border: 'none', cursor: 'pointer', background: `${COLORS.blue}22`, color: COLORS.blue, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15 }} onMouseEnter={e => e.currentTarget.style.background = COLORS.blue + '44'} onMouseLeave={e => e.currentTarget.style.background = COLORS.blue + '22'}><i className="ti ti-edit" /></button>
                          <button onClick={() => handleDelete(item.id)} title="Delete" style={{ width: 30, height: 30, borderRadius: 8, border: 'none', cursor: 'pointer', background: `${COLORS.red}18`, color: COLORS.red, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15 }} onMouseEnter={e => e.currentTarget.style.background = COLORS.red + '33'} onMouseLeave={e => e.currentTarget.style.background = COLORS.red + '18'}><i className="ti ti-trash" /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div style={{ display: 'flex', justifyContent: 'center', gap: 6, marginTop: 16, flexWrap: 'wrap' }}>
                <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} style={{ height: 32, minWidth: 32, padding: '0 10px', borderRadius: 8, border: '1px solid #dde3eb', background: currentPage === 1 ? '#f8f9fb' : '#fff', color: currentPage === 1 ? '#bbb' : COLORS.gray, cursor: currentPage === 1 ? 'not-allowed' : 'pointer', fontSize: 13 }}><i className="ti ti-chevron-left" /></button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
                  <button key={p} onClick={() => setCurrentPage(p)} style={{ height: 32, minWidth: 32, padding: '0 10px', borderRadius: 8, border: `1px solid ${p === currentPage ? COLORS.blue : '#dde3eb'}`, background: p === currentPage ? `${COLORS.blue}22` : '#fff', color: p === currentPage ? COLORS.blue : COLORS.gray, cursor: 'pointer', fontSize: 13, fontWeight: p === currentPage ? 700 : 400 }}>{p}</button>
                ))}
                <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} style={{ height: 32, minWidth: 32, padding: '0 10px', borderRadius: 8, border: '1px solid #dde3eb', background: currentPage === totalPages ? '#f8f9fb' : '#fff', color: currentPage === totalPages ? '#bbb' : COLORS.gray, cursor: currentPage === totalPages ? 'not-allowed' : 'pointer', fontSize: 13 }}><i className="ti ti-chevron-right" /></button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
